// src/lib/subscriptionService.ts
import { supabase } from './supabase';

/**
 * Types for payment method details
 */
export interface PaymentMethodDetails {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

/**
 * Service for subscription-related operations
 */
/**
 * Types for payment method details
 */
export interface PaymentMethodDetails {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

// Cache for subscription settings
interface PaywallSettingsCache {
  settings: {
    paywall_enabled: boolean;
    grace_period_days: number;
    test_mode: boolean;
  };
  timestamp: number;
  expiresAt: number;
}

// Store cache outside of object to avoid typescript issues with static properties
let paywallSettingsCache: PaywallSettingsCache | null = null;

export const subscriptionService = {

  /**
   * Get system settings for subscription paywall
   * @param forceRefresh If true, bypass the cache and fetch fresh data
   */
  async getPaywallSettings(forceRefresh = false) {
    // Check if we have cached settings that are still valid (less than 5 minutes old)
    const now = new Date().getTime();

    if (!forceRefresh &&
      paywallSettingsCache &&
      now < paywallSettingsCache.expiresAt) {
      // Use cached value
      return paywallSettingsCache.settings;
    }

    try {
      // Use .limit(1) to ensure we get exactly the right record
      const timestamp = now;
      const { data, error } = await supabase
        .from('system_settings')
        .select('setting_value, updated_at')
        .eq('setting_key', 'subscription_settings')
        .limit(1)
        .order('updated_at', { ascending: false })
        .maybeSingle();

      if (error) {
        console.error('Database error fetching settings:', error);
        throw error;
      }

      if (!data || !data.setting_value) {
        console.log('No settings found, creating defaults');

        // Create default settings since none exist
        const defaultSettings = {
          paywall_enabled: false,
          grace_period_days: 3,
          test_mode: true
        };

        try {
          const { data: user } = await supabase.auth.getUser();
          if (user && user.user) {
            // Try to create default settings
            const { error: insertError } = await supabase
              .from('system_settings')
              .insert({
                setting_key: 'subscription_settings',
                setting_value: defaultSettings,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                created_by: user.user.id,
                updated_by: user.user.id
              });

            if (insertError) {
              console.warn('Could not save default settings:', insertError);
            } else {
              console.log('Created default settings successfully');
            }
          }
        } catch (err) {
          console.warn('Error creating default settings:', err);
        }

        return defaultSettings;
      }

      // Log the raw data we got from the database for debugging
      // Only log when forced refresh to reduce noise
      if (forceRefresh) {
        console.log(`Settings from DB (timestamp: ${timestamp}):`, {
          setting_value: data.setting_value,
          updated_at: data.updated_at
        });
      }

      // Prepare the settings object to return
      const settings = {
        paywall_enabled: data.setting_value.paywall_enabled === true,
        grace_period_days: data.setting_value.grace_period_days || 3,
        test_mode: data.setting_value.test_mode === true
      };

      // Store in cache for 5 minutes
      paywallSettingsCache = {
        settings,
        timestamp,
        expiresAt: timestamp + (5 * 60 * 1000) // 5 minutes
      };

      return settings;
    } catch (error) {
      console.error('Error fetching paywall settings:', error);
      // Default to disabled on error for safety
      return { paywall_enabled: false, grace_period_days: 3, test_mode: true };
    }
  },

  /**
   * Check subscription status for a user
   */
  async getUserSubscriptionStatus(userId: string) {
    if (!userId) return null;

    try {
      // First check if paywall is enabled globally
      const settings = await this.getPaywallSettings();

      if (!settings.paywall_enabled) {
        // Only log in development
        if (process.env.NODE_ENV !== 'production') {
          console.log('Paywall globally disabled, bypassing subscription check');
        }
        return 'bypass';
      }

      // Skip the RPC completely - it's failing with network errors
      // Go directly to the reliable direct table access method
      return await this.getSubscriptionStatusDirect(userId);
    } catch (error) {
      console.error('Error checking subscription:', error);
      return 'inactive'; // Default to inactive on error
    }
  },

  /**
   * Fallback method that directly queries the user_subscriptions table
   * Used only if the secure RPC method fails
   * @private
   */
  async getSubscriptionStatusDirect(userId: string) {
    try {
      // Only log in development
      if (process.env.NODE_ENV !== 'production') {
        console.log('Using direct subscription status check for user:', userId);
      }

      // Add retry logic for robustness
      let retries = 2;
      let data = null;
      let error = null;

      while (retries >= 0) {
        // Use maybeSingle() instead of single() to prevent 406 errors
        // maybeSingle() will return null instead of error if no records found
        const result = await supabase
          .from('user_subscriptions')
          .select('subscription_status, bypass_subscription, plan_type, current_period_end, stripe_subscription_id')
          .eq('user_id', userId)
          .maybeSingle();

        if (!result.error || result.error.code === 'PGSQL_ERROR_NO_DATA') {
          data = result.data;
          error = result.error;
          break; // Success or no data, exit retry loop
        }

        error = result.error;
        retries--;
        if (retries >= 0) {
          // Wait before retry (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, 500 * (2 - retries)));
        }
      }

      if (error && error.code !== 'PGSQL_ERROR_NO_DATA') {
        console.error('Direct subscription query failed after retries:', error);
        return 'inactive';
      }

      if (!data) {
        if (process.env.NODE_ENV !== 'production') {
          console.log('No subscription record found');
        }
        return 'inactive';
      } else if (data.bypass_subscription) {
        if (process.env.NODE_ENV !== 'production') {
          console.log('User has bypass subscription enabled');
        }
        return 'bypass';
      } else if (data.subscription_status === 'active') {
        if (process.env.NODE_ENV !== 'production') {
          console.log('User has active subscription', {
            has_placeholder: data.stripe_subscription_id?.includes('placeholder') || false,
            plan_type: data.plan_type
          });
        }
        // Consider placeholder subscription IDs as valid
        return 'active';
      } else if (data.plan_type === 'free') {
        if (process.env.NODE_ENV !== 'production') {
          console.log('User has free tier subscription');
        }
        return 'active';
      } else {
        console.log('User has inactive subscription status:', data.subscription_status);
        return 'inactive';
      }
    } catch (error) {
      console.error('Error in direct subscription check:', error);
      return 'inactive';
    }
  },

  /**
   * Get detailed subscription data for a user
   */
  async getUserSubscriptionDetails(userId: string) {
    if (!userId) return null;

    try {
      // Use the secure RPC function to get subscription details
      // This avoids permission issues with auth.users
      const { data, error } = await supabase
        .rpc('get_user_subscription_details', {
          target_user_id: userId
        });

      if (error) {
        console.error('Error calling get_user_subscription_details RPC:', error);
        // Fall back to direct method if RPC fails
        return await this.getSubscriptionDetailsDirect(userId);
      }

      return data;
    } catch (error) {
      console.error('Exception in getUserSubscriptionDetails:', error);
      return null;
    }
  },

  /**
   * Fallback method that directly queries the user_subscriptions table
   * Used only if the secure RPC method fails
   * @private
   */
  async getSubscriptionDetailsDirect(userId: string) {
    try {
      console.log('Using direct subscription details check for user:', userId);

      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        if (error.code === 'PGSQL_ERROR_NO_DATA') {
          console.log('No subscription record found for user:', userId);
          return null;
        }

        console.error('Error fetching subscription details directly:', error);
        // Try to log detailed information about the error
        if (error.message) console.error('Error message:', error.message);
        if (error.details) console.error('Error details:', error.details);
        if (error.hint) console.error('Error hint:', error.hint);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Exception in getSubscriptionDetailsDirect:', error);
      return null;
    }
  },

  /**
   * Enable bypass mode for a user (for testing)
   */
  async enableBypassMode(userId: string) {
    if (!userId) return false;

    try {
      // Check if user_subscriptions record exists
      const { data: existingSub, error: checkError } = await supabase
        .from('user_subscriptions')
        .select('id')
        .eq('user_id', userId)
        .single();

      if (checkError && checkError.code !== 'PGSQL_ERROR_NO_DATA') throw checkError;

      if (existingSub) {
        // Update existing record
        const { error } = await supabase
          .from('user_subscriptions')
          .update({
            bypass_subscription: true,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId);

        if (error) throw error;
      } else {
        // Create new record with bypass
        const { error } = await supabase
          .from('user_subscriptions')
          .insert({
            user_id: userId,
            subscription_status: 'inactive',
            bypass_subscription: true,
            plan_type: 'bypass',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (error) throw error;
      }

      return true;
    } catch (error) {
      console.error('Error enabling bypass mode:', error);
      return false;
    }
  },

  /**
   * Initialize a free tier subscription for a user
   */
  async initializeFreeSubscription(userId: string) {
    if (!userId) return false;

    try {
      console.log('Initializing free subscription for user:', userId);

      // Check if user_subscriptions record exists
      const { data: existingSub, error: checkError } = await supabase
        .from('user_subscriptions')
        .select('id, subscription_status')
        .eq('user_id', userId)
        .maybeSingle();

      if (checkError) throw checkError;

      if (existingSub) {
        // If already active on a paid plan, don't downgrade automatically
        if (existingSub.subscription_status === 'active' && (existingSub as any).plan_type !== 'free') {
          console.log('User already has an active paid subscription');
          return true;
        }

        // Update existing record
        const { error } = await supabase
          .from('user_subscriptions')
          .update({
            subscription_status: 'active',
            plan_type: 'free',
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId);

        if (error) throw error;
      } else {
        // Create new record for free tier
        const { error } = await supabase
          .from('user_subscriptions')
          .insert({
            user_id: userId,
            subscription_status: 'active',
            plan_type: 'free',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (error) throw error;
      }

      return true;
    } catch (error) {
      console.error('Error initializing free subscription:', error);
      return false;
    }
  },

  /**
   * Update system settings
   */
  async updatePaywallSettings(settings: {
    paywall_enabled: boolean,
    grace_period_days: number,
    test_mode: boolean
  }) {
    try {
      console.log('Updating settings to:', settings);

      // Get current user ID for the updated_by field
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Use the secure function to update settings
      const { data, error } = await supabase
        .rpc('update_system_settings', {
          p_setting_key: 'subscription_settings',
          p_setting_value: settings,
          p_updated_by: user.id
        });

      if (error) {
        console.error('Error calling update_system_settings RPC:', error);
        throw error;
      }

      console.log('Settings successfully updated via secure function');
      const result = { data, error: null };

      if (result.error) {
        console.error('Database error during settings update:', result.error);
        throw result.error;
      }

      // Verify the settings were saved correctly
      const updatedSettings = await this.getPaywallSettings();
      console.log('Verified saved settings:', updatedSettings);

      return true;
    } catch (error) {
      console.error('Error updating paywall settings:', error);
      return false;
    }
  },

  /**
   * Get the user's payment method details from Stripe
   * @param userId User ID to lookup payment method for
   * @returns Payment method details or null if not found
   */
  async getUserPaymentMethod(userId: string): Promise<PaymentMethodDetails | null> {
    try {
      const response = await fetch('/api/functions/v1/get-payment-method', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({ userId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error fetching payment method:', errorData);
        return null;
      }

      const data = await response.json();
      return data.paymentMethod || null;
    } catch (error) {
      console.error('Error fetching payment method:', error);
      return null;
    }
  }
};
